import { useTranslation } from '../../contexts/LanguageContext'

interface ImportProgressProps {
  progress: number
  status: string
}

export function ImportProgress({ progress, status }: ImportProgressProps) {
  const { t } = useTranslation()

  return (
    <div className="text-center py-8">
      {/* Animated loader */}
      <div className="relative w-20 h-20 mx-auto mb-5">
        <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="#F3F4F6"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            className="text-primary"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${progress * 2.51} 251`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-gray-900">{progress}%</span>
        </div>
      </div>

      {/* Status text */}
      <p className="font-display text-base font-semibold text-gray-900">{status}</p>
      <p className="text-sm text-gray-500 mt-1">{t.import.mayTakeSeconds}</p>

      {/* Steps */}
      <div className="mt-6 flex items-center justify-center gap-2.5">
        <Step label={t.import.stepReading} completed={progress >= 30} active={progress < 30} />
        <Connector />
        <Step label={t.import.stepExtracting} completed={progress >= 50} active={progress >= 30 && progress < 50} />
        <Connector />
        <Step label={t.import.stepAnalyzing} completed={progress >= 90} active={progress >= 50 && progress < 90} />
        <Connector />
        <Step label={t.import.stepDone} completed={progress >= 100} active={progress >= 90 && progress < 100} />
      </div>
    </div>
  )
}

function Connector() {
  return <div className="w-5 h-px bg-gray-200" />
}

interface StepProps {
  label: string
  completed: boolean
  active: boolean
}

function Step({ label, completed, active }: StepProps) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
          completed
            ? 'bg-emerald-500'
            : active
              ? 'bg-primary'
              : 'bg-gray-200'
        }`}
      >
        {completed ? (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <div className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white animate-pulse' : 'bg-gray-400'}`} />
        )}
      </div>
      <span className={`text-xs ${completed || active ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
        {label}
      </span>
    </div>
  )
}
